import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { admin, api, ApiError } from '../api/client'
import type { ProjectDetail, ProjectRole, Station } from '../api/types'
import { VENDOR_LABEL } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { useAuth } from '../lib/auth'
import { filterStations } from '../lib/stationSearch'
import { formatDateTime } from '../lib/time'
import { canArchive, canEdit, canManagePeople, roleLabel } from '../lib/projects'
import {
  type FormState, formChanges, heldForm, listedPerson, missingLine, pickablePeople, nameTaken, pickableStations,
  projectExportName, saveFailure,
} from '../lib/projectForm'
import { initials } from '../components/UserMenu'

const EMPTY: FormState = {
  name: '', client: '', description: '', timezone: 'Asia/Tbilisi', crs_epsg: 32638,
  stations: [], members: [],
}

function fromDetail(detail: ProjectDetail): FormState {
  const { project, stations, members } = detail
  return {
    name: project.name, client: project.client, description: project.description,
    timezone: project.timezone, crs_epsg: project.crs_epsg,
    stations: stations.map((s) => s.code),
    members: members.map((m) => ({ email: m.email, role: m.role })),
  }
}

const message = (failed: unknown) => (failed instanceof Error ? failed.message : 'Something went wrong.')
const statusOf = (failed: unknown) => (failed instanceof ApiError ? failed.status : undefined)

/** Create a project (`/projects/new`, platform admins) or edit one
 *  (`/projects/:id/edit`, the project's Editors and Admins), as the
 *  NewProject board draws it. Each section is enabled by the reader's role
 *  in the project; the server checks every write again. */
export function ProjectForm() {
  const { id } = useParams()
  const editing = id !== undefined
  const projectId = Number(id)
  const { user } = useAuth()
  const navigate = useNavigate()
  const stations = useAsync(() => api.stations(), [])
  const projects = useAsync(() => api.projects.list(), [])
  const detail = useAsync(
    () => (editing ? api.projects.get(projectId) : Promise.resolve(undefined)), [id])

  const original = useMemo<FormState | null>(() => {
    if (editing) return detail.data ? fromDetail(detail.data) : null
    return { ...EMPTY, members: user ? [{ email: user.email, role: 'admin' }] : [] }
  }, [editing, detail.data, user])
  // The reader's edits stay theirs while they are on this project, whatever
  // a re-read of it (or of who they are) brings; another project starts over.
  const formKey = editing ? `edit:${id}` : 'new'
  const [held, setHeld] = useState<{ key: string; form: FormState } | null>(null)
  const form = heldForm(held, formKey, original)
  const setForm = (next: FormState) => setHeld({ key: formKey, form: next })

  const role: ProjectRole | undefined = editing ? detail.data?.my_role : 'admin'

  if (editing && detail.error) {
    const missing = detail.error.includes('404') || /no such project/i.test(detail.error)
    return <div className="page"><p className="error">{missing ? 'No such project.' : detail.error}</p></div>
  }
  if (!form || !role || !stations.data) return <div className="page"><p className="muted">Loading…</p></div>
  if (!canEdit(role)) {
    return (
      <div className="page">
        <p className="error">No access: changing this project needs the project Editor or Admin role.</p>
      </div>
    )
  }
  return (
    <FormBody key={id ?? 'new'} editing={editing} projectId={projectId} role={role}
              original={original!} form={form} setForm={setForm}
              stations={pickableStations(stations.data, detail.data?.stations ?? [])}
              projects={projects.data?.projects ?? []} myEmail={user?.email ?? ''}
              onDone={(target) => navigate(target)} />
  )
}

function FormBody({ editing, projectId, role, original, form, setForm, stations, projects,
                    myEmail, onDone }: {
  editing: boolean; projectId: number; role: ProjectRole; original: FormState
  form: FormState; setForm: (next: FormState) => void; stations: Station[]
  projects: { id: number; name: string }[]; myEmail: string; onDone: (to: string) => void
}) {
  const [stQuery, setStQuery] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState<ProjectRole>('viewer')
  const [emailError, setEmailError] = useState<string>()
  const [nameError, setNameError] = useState<string>()
  const [saveError, setSaveError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)

  const people = canManagePeople(role)
  // The accounts a person can be chosen from (PM-32): active ones only, so no
  // address is typed. A platform admin's list is everyone; a project Admin's,
  // the same for their project.
  const directory = useAsync(
    () => people ? api.projects.people(editing ? projectId : undefined) : Promise.resolve({ people: [] }),
    [people, projectId, editing])
  const taken = nameTaken(form.name, projects, editing ? projectId : undefined)
  const missing = missingLine(form.name, form.stations.length, taken)
  const shown = filterStations(stations, stQuery)
  const set = (patch: Partial<FormState>) => setForm({ ...form, ...patch })
  const toggle = (code: string) => set({
    stations: form.stations.includes(code)
      ? form.stations.filter((c) => c !== code) : [...form.stations, code] })

  const today = formatDateTime(new Date()).slice(0, 10)
  const weekAgo = formatDateTime(new Date(Date.now() - 6 * 86_400_000)).slice(0, 10)
  const firstStation = stations.find((s) => s.code === form.stations[0])?.name ?? 'Station'
  const exportName = projectExportName(form.name || 'Project', firstStation, weekAgo, today)

  const addMember = () => {
    const email = newEmail.trim()
    const person = listedPerson(directory.data?.people ?? [], email)
    if (!person) { setEmailError('Choose a person from the list.'); return }
    if (form.members.some((m) => m.email.toLowerCase() === email.toLowerCase())) {
      setEmailError(`${email} is already in this project.`); return
    }
    setEmailError(undefined)
    set({ members: [...form.members, { email: person.email, role: newRole }] })
    setNewEmail('')
  }

  const save = async () => {
    setBusy(true); setSaveError(undefined); setNameError(undefined); setEmailError(undefined)
    // Each failure is said where it belongs (lib/projectForm saveFailure).
    const show = (part: string | null, failed: unknown, saved: string[]) => {
      const { field, text } = saveFailure(part, statusOf(failed), message(failed), saved)
      const setters = { name: setNameError, email: setEmailError, form: setSaveError }
      setters[field](text)
    }
    try {
      if (!editing) {
        try {
          const made = await admin.createProject({
            name: form.name, client: form.client, description: form.description,
            timezone: form.timezone, crs_epsg: form.crs_epsg, stations: form.stations,
            members: form.members })
          onDone(`/projects?selected=${made.project.id}`)
        } catch (failed) {
          show(null, failed, [])
        }
        return
      }
      const changes = formChanges(original, form)
      const saved: string[] = []
      const steps: [string, (() => Promise<unknown>) | null][] = [
        ['details', changes.basics && (() => api.projects.update(projectId, changes.basics!))],
        ['stations', changes.stations && (() => api.projects.setStations(projectId, changes.stations!))],
        ['people', people && changes.members ? () => api.projects.setMembers(projectId, changes.members!) : null],
      ]
      for (const [part, run] of steps) {
        if (!run) continue
        try {
          await run()
          saved.push(part)
        } catch (failed) {
          show(part, failed, saved)
          return
        }
      }
      onDone(`/projects?selected=${projectId}`)
    } finally {
      setBusy(false)
    }
  }

  const archive = async () => {
    setBusy(true)
    try {
      await api.projects.archive(projectId)
      onDone('/projects')
    } catch (failed) {
      setSaveError(message(failed))
      setBusy(false)
    }
  }

  return (
    <div className="page pf-page">
      <div className="pf-head">
        <div className="muted"><Link to="/projects">Projects</Link> / {editing ? original.name : 'New project'}</div>
        <h1>{editing ? 'Edit Project' : 'New Project'}</h1>
        <p className="muted">
          A project groups stations and people, for example one client contract or one monitoring
          programme. Home keeps showing every station whatever project it is in.
        </p>
      </div>

      <section className="pf-section" aria-labelledby="pf-basics">
        <h2 id="pf-basics">Basics</h2>
        <div className="pf-two">
          <label className="field">
            <span>Project name</span>
            <input type="text" value={form.name} aria-required="true" aria-invalid={taken || !!nameError}
                   placeholder="For example, Enguri dam monitoring" maxLength={80}
                   onChange={(e) => set({ name: e.target.value })} />
            {(taken || nameError) && (
              <small className="error" role="alert">
                {nameError ?? `A project named ${form.name.trim()} already exists.`}
              </small>
            )}
            <small className="muted">Must be unique. Exports are named after it: <span className="mono">{exportName}</span></small>
          </label>
          <label className="field">
            <span>Client or owner</span>
            <input type="text" value={form.client} placeholder="Organisation name" maxLength={120}
                   onChange={(e) => set({ client: e.target.value })} />
          </label>
        </div>
        <label className="field">
          <span>Description</span>
          <textarea rows={3} value={form.description} maxLength={2000}
                    placeholder="What the project monitors and why"
                    onChange={(e) => set({ description: e.target.value })} />
        </label>
      </section>

      <section className="pf-section" aria-labelledby="pf-where">
        <h2 id="pf-where">Time and Coordinates</h2>
        <div className="pf-two">
          <label className="field">
            <span>Time zone</span>
            <select value={form.timezone}
                    onChange={(e) => set({ timezone: e.target.value as FormState['timezone'] })}>
              <option value="Asia/Tbilisi">Tbilisi, UTC+4</option>
              <option value="UTC">UTC</option>
            </select>
            <small className="muted pf-hint">
              Kept with the project. Screens and exports still show station time, Tbilisi, UTC+4.
            </small>
          </label>
          <label className="field">
            <span>Grid for northing and easting</span>
            <select value={form.crs_epsg}
                    onChange={(e) => set({ crs_epsg: Number(e.target.value) as FormState['crs_epsg'] })}>
              <option value={32638}>WGS 84 / UTM zone 38N (EPSG:32638)</option>
              <option value={32637}>WGS 84 / UTM zone 37N (EPSG:32637)</option>
              <option value={4326}>Latitude and longitude only (EPSG:4326)</option>
            </select>
          </label>
        </div>
      </section>

      <section className="pf-section" aria-labelledby="pf-stations">
        <div className="pf-section-head">
          <div>
            <h2 id="pf-stations">Stations</h2>
            <span className="muted">
              {form.stations.length
                ? `${form.stations.length} ${form.stations.length === 1 ? 'station' : 'stations'} picked.`
                : 'Pick the stations this project covers. A station can sit in more than one project.'}
            </span>
          </div>
          <input type="search" aria-label="Search stations" placeholder="Name or code" value={stQuery}
                 onChange={(e) => setStQuery(e.target.value)} />
        </div>
        <div className="pf-picks">
          {shown.map((station) => {
            const others = (station.projects ?? []).filter((p) => p !== original.name || !editing)
            return (
              <label key={station.code} className="pf-pick">
                <input type="checkbox" checked={form.stations.includes(station.code)}
                       onChange={() => toggle(station.code)} />
                <span>
                  <span className="pf-pick-name">{station.name}</span>
                  <span className="muted">
                    <span className="mono">{station.code}</span> · {VENDOR_LABEL[station.vendor]}
                    {!station.active && ' · Decommissioned'}
                  </span>
                </span>
                {others.length > 0 && <span className="muted pf-also">Also in {others.join(', ')}</span>}
              </label>
            )
          })}
        </div>
      </section>

      <section className="pf-section" aria-labelledby="pf-people">
        <div>
          <h2 id="pf-people">People</h2>
          <span className="muted">
            Choose from the people who have signed in and been approved; someone who has not, or is
            waiting for approval, is not on the list. Viewers see
            the project; editors also change its details and stations; admins also change its people
            and archive it.
          </span>
        </div>
        {!people && (
          <p className="muted">Only a project admin can change who is in this project.</p>
        )}
        <div className="pf-members">
          {form.members.map((member) => {
            const me = member.email.toLowerCase() === myEmail.toLowerCase()
            return (
              <div key={member.email} className="pf-member">
                <span className="pj-avatar" aria-hidden="true">{initials(member.email)}</span>
                <span className="pf-member-name">{member.email}{me ? ' (you)' : ''}</span>
                {people && !(me && !editing) ? (
                  <select aria-label={`Role for ${member.email}`} value={member.role}
                          onChange={(e) => set({ members: form.members.map((m) =>
                            m.email === member.email ? { ...m, role: e.target.value as ProjectRole } : m) })}>
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : <span className="muted">{roleLabel(member.role)}</span>}
                {people && !(me && !editing) && (
                  <button type="button" className="button" aria-label={`Remove ${member.email}`}
                          onClick={() => set({ members: form.members.filter((m) => m.email !== member.email) })}>
                    Remove
                  </button>
                )}
              </div>
            )
          })}
        </div>
        {people && (
          <div className="pf-add">
            <label className="field">
              <span>Person</span>
              <select value={newEmail} disabled={directory.loading}
                      onChange={(e) => { setNewEmail(e.target.value); setEmailError(undefined) }}>
                <option value="">{directory.loading ? 'Loading people…' : 'Choose a person'}</option>
                {pickablePeople(directory.data?.people ?? [], form.members).map((person) => (
                  <option key={person.id} value={person.email}>{person.name} · {person.email}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Role</span>
              <select value={newRole} onChange={(e) => setNewRole(e.target.value as ProjectRole)}>
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button type="button" className="button" disabled={!newEmail || directory.loading}
                    onClick={addMember}>Add</button>
          </div>
        )}
        {people && directory.error && <p className="error" role="alert">{directory.error}{' '}
          <button type="button" className="link" onClick={directory.reload}>Try again</button></p>}
        {emailError && <p className="error" role="alert">{emailError}</p>}
      </section>

      <section className="pf-section" aria-labelledby="pf-rules">
        <h2 id="pf-rules">Data Rules</h2>
        <p className="muted">
          A station shows No data after 3× its expected interval, the same everywhere. Alerts for its
          stations and readings are set up in{' '}
          <Link to={editing ? `/alerts?project=${projectId}` : '/alerts'}>Alerts</Link>.
        </p>
      </section>

      {saveError && <p className="error" role="alert">{saveError}</p>}
      <div className="pf-actions">
        {missing && <span className="muted">{missing}</span>}
        <Link className="button" to={editing ? `/projects?selected=${projectId}` : '/projects'}>Cancel</Link>
        <button type="button" className="button primary" disabled={!!missing || busy} onClick={save}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Create project'}
        </button>
      </div>

      {editing && canArchive(role) && (
        <div className="pf-archive">
          {confirmArchive ? (
            <>
              <span>Archiving hides it from every list. Its stations and their data stay as they are.</span>
              <button type="button" className="button danger" disabled={busy} onClick={archive}>
                Archive {original.name}
              </button>
              <button type="button" className="button" onClick={() => setConfirmArchive(false)}>Keep it</button>
            </>
          ) : (
            <button type="button" className="link danger" onClick={() => setConfirmArchive(true)}>
              Archive project
            </button>
          )}
        </div>
      )}
    </div>
  )
}
